import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { IoSendSharp, IoChatbubble } from 'react-icons/io5';
import { Fonts, Palette } from '../../components/utils';
import {
  clearChatThread,
  getChatMessages,
  markChatConversationRead,
  sendChatMessage
} from '../../redux/actions/chat';
import { setToast } from '../../redux/actions/window';
import { requestPending, requestSuccess, requestFail } from '../../utils/status';
import { GET_CHAT_MESSAGES, SEND_CHAT_MESSAGE } from '../../redux/constants';
import { chatErrorMessage } from '../../helper/chat';
import { CHAT_MESSAGES_PAGE_SIZE } from './constants';
import LoadingAvatar from '../../components/LoadingAvatar';
import { LOADING_BLUE } from '../../utils/colorCode';
import ChatAvatar from './ChatAvatar';
import ChatPanelHeader from './ChatPanelHeader';
import { ChatEmptyText, ChatOlderLoadingWrap } from './chatSharedStyles';
import { preloadChatAvatars } from './chatAvatarUtils';

const NEAR_BOTTOM_PX = 80;
const LOAD_OLDER_TOP_PX = 80;
const MAX_CHAT_BODY_LENGTH = 4000;

const makeClientMsgId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const m = moment(iso);
  if (!m.isValid()) return '';
  const time = m.format('h:mm A');
  const mins = moment().diff(m, 'minutes');
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (m.isSame(moment(), 'day')) return time;
  if (m.isSame(moment().subtract(1, 'day'), 'day')) return `Yesterday, ${time}`;
  if (m.isSame(moment(), 'year')) return `${m.format('MMM D')}, ${time}`;
  return `${m.format('MMM D, YYYY')}, ${time}`;
};

const ChatThread = ({ conversation, onBack, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const listRef = useRef(null);
  const textAreaRef = useRef(null);
  const lastMarkedMessageId = useRef(null);
  const scrollModeRef = useRef('bottom'); // 'bottom' | 'preserve' | 'none'
  const scrollSnapshotRef = useRef(null);
  const conversationIdRef = useRef(conversation?.id);
  conversationIdRef.current = conversation?.id;
  const [draft, setDraft] = useState('');
  const [avatarsReady, setAvatarsReady] = useState(false);

  const authUserId = useSelector((state) => state.auth.user_id);
  const { messages, messagesStatus, messagesError, messagesHasMore, sendStatus, sendConversationId } =
    useSelector((state) => state.chat);

  const currentUserId = Number(authUserId || localStorage.getItem('user_id')) || 0;

  const title = conversation?.resident_name || conversation?.title || t('Conversation');
  const unitLabel = conversation?.unit_num ? `Unit ${conversation.unit_num}` : null;

  const isInitialLoading =
    messagesStatus === requestPending(GET_CHAT_MESSAGES) && messages.length === 0;
  const isLoadingOlder =
    messagesStatus === requestPending(GET_CHAT_MESSAGES) && messages.length > 0;
  const isFailed = messagesStatus === requestFail(GET_CHAT_MESSAGES) && messages.length === 0;
  const messagesLoaded = messagesStatus === requestSuccess(GET_CHAT_MESSAGES);
  const sending =
    sendStatus === requestPending(SEND_CHAT_MESSAGE) &&
    (!sendConversationId || Number(sendConversationId) === Number(conversation?.id));
  const isEmpty = !isInitialLoading && !isFailed && messages.length === 0;

  const messageAvatarKey = useMemo(
    () => (messages || []).map((m) => `${m.id}:${m.sender_avatar || ''}`).join('|'),
    [messages]
  );

  // Reset avatar gate and composer when switching conversations only.
  useEffect(() => {
    setAvatarsReady(false);
    setDraft('');
  }, [conversation?.id]);

  useEffect(() => {
    if (!messagesLoaded) return undefined;
    if (messages.length === 0) {
      setAvatarsReady(true);
      return undefined;
    }

    let cancelled = false;
    preloadChatAvatars(messages.map((m) => m.sender_avatar)).then(() => {
      if (!cancelled) setAvatarsReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [messagesLoaded, messageAvatarKey, messages]);

  const showMessagesLoading =
    isInitialLoading || (messages.length > 0 && !avatarsReady && !isLoadingOlder);

  useEffect(() => {
    if (!conversation?.id) return;
    lastMarkedMessageId.current = null;
    scrollModeRef.current = 'bottom';
    scrollSnapshotRef.current = null;
    dispatch(
      getChatMessages({
        id: conversation.id,
        before_id: 0,
        limit: CHAT_MESSAGES_PAGE_SIZE
      })
    );
  }, [conversation?.id, dispatch]);

  // Mark read on open and again whenever a newer message appears while this thread is open.
  useEffect(() => {
    if (messagesStatus !== requestSuccess(GET_CHAT_MESSAGES)) return;
    if (!conversation?.id || !messages.length) return;

    const latest = messages[messages.length - 1];
    if (!latest?.id) return;
    if (lastMarkedMessageId.current === Number(latest.id)) return;

    lastMarkedMessageId.current = Number(latest.id);
    dispatch(
      markChatConversationRead({
        id: conversation.id,
        body: { last_read_message_id: latest.id }
      })
    );
  }, [messagesStatus, messages, conversation?.id, dispatch]);

  // Scroll: stick to bottom on open/new tip messages; preserve position when prepending older.
  // Wait until message rows are painted (avatarsReady) so we do not clear "bottom" on the loader height.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (showMessagesLoading) return;

    const stickBottom = () => {
      el.scrollTop = el.scrollHeight;
    };

    if (scrollModeRef.current === 'preserve' && scrollSnapshotRef.current) {
      const { height, top } = scrollSnapshotRef.current;
      el.scrollTop = top + (el.scrollHeight - height);
      scrollModeRef.current = 'none';
      scrollSnapshotRef.current = null;
      return;
    }

    if (scrollModeRef.current === 'bottom') {
      stickBottom();
      // After layout (avatars/images), jump again once so the latest message is visible.
      const raf = requestAnimationFrame(() => {
        stickBottom();
        if (messagesStatus === requestSuccess(GET_CHAT_MESSAGES)) {
          scrollModeRef.current = 'none';
        }
      });
      return () => cancelAnimationFrame(raf);
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < NEAR_BOTTOM_PX) {
      stickBottom();
    }
  }, [messages, messagesStatus, showMessagesLoading, avatarsReady]);

  const loadOlderMessages = useCallback(() => {
    if (!conversation?.id) return;
    if (!messagesHasMore || isLoadingOlder || isInitialLoading) return;
    if (showMessagesLoading) return;
    const oldest = messages[0];
    if (!oldest?.id) return;

    const el = listRef.current;
    if (el) {
      scrollModeRef.current = 'preserve';
      scrollSnapshotRef.current = { height: el.scrollHeight, top: el.scrollTop };
    }

    dispatch(
      getChatMessages({
        id: conversation.id,
        before_id: Number(oldest.id),
        limit: CHAT_MESSAGES_PAGE_SIZE,
        fail: (err) => {
          scrollModeRef.current = 'none';
          scrollSnapshotRef.current = null;
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: chatErrorMessage(err, t('Failed to load older messages')),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  }, [
    conversation?.id,
    messages,
    messagesHasMore,
    isLoadingOlder,
    isInitialLoading,
    showMessagesLoading,
    dispatch,
    t
  ]);

  // If the latest page does not fill the viewport, keep pulling older history.
  // Only measure after message rows are painted (not during the avatar loader).
  useEffect(() => {
    if (messagesStatus !== requestSuccess(GET_CHAT_MESSAGES)) return undefined;
    if (showMessagesLoading) return undefined;
    if (!messagesHasMore || !messages.length) return undefined;
    const el = listRef.current;
    if (!el) return undefined;

    let cancelled = false;
    const tryLoadOlder = () => {
      if (cancelled) return;
      if (el.scrollHeight > el.clientHeight + 1) return;
      loadOlderMessages();
    };

    tryLoadOlder();
    const raf = requestAnimationFrame(tryLoadOlder);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [
    messagesStatus,
    messagesHasMore,
    messages.length,
    showMessagesLoading,
    avatarsReady,
    loadOlderMessages
  ]);

  const handleMessagesScroll = () => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollTop > LOAD_OLDER_TOP_PX) return;
    loadOlderMessages();
  };

  const handleBack = () => {
    dispatch(clearChatThread());
    onBack();
  };

  const handleClose = () => {
    dispatch(clearChatThread());
    onClose();
  };

  const resizeTextarea = () => {
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const handleSend = (e) => {
    if (e) e.preventDefault();
    const body = draft.trim();
    if (!body || !conversation?.id) return;
    if (sending) return;

    if (body.length > MAX_CHAT_BODY_LENGTH) {
      dispatch(
        setToast({
          toastShow: true,
          toastMessage: t('Message is too long (max {{max}} characters)', {
            max: MAX_CHAT_BODY_LENGTH
          }),
          modal: 'errorToast'
        })
      );
      return;
    }

    scrollModeRef.current = 'bottom';
    const sentConversationId = Number(conversation.id);
    dispatch(
      sendChatMessage({
        id: conversation.id,
        body: {
          body,
          client_msg_id: makeClientMsgId()
        },
        success: () => {
          // Ignore if the user already switched threads.
          if (Number(conversationIdRef.current) !== sentConversationId) return;
          setDraft('');
          if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';
          }
        },
        fail: (err) => {
          if (Number(conversationIdRef.current) !== sentConversationId) return;
          dispatch(
            setToast({
              toastShow: true,
              toastMessage: chatErrorMessage(err, t('Failed to send message')),
              modal: 'errorToast'
            })
          );
        }
      })
    );
  };

  const submitOnEnter = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isMessageSent = (message) => {
    if (message.sender_user_id == null || !currentUserId) return false;
    return Number(message.sender_user_id) === currentUserId;
  };

  return (
    <ThreadRoot>
      <ChatPanelHeader
        title={title}
        subtitle={unitLabel}
        compact
        onBack={handleBack}
        onClose={handleClose}
      />

      <MessagesArea
        ref={listRef}
        $empty={isEmpty || showMessagesLoading || isFailed}
        onScroll={handleMessagesScroll}
      >
        {showMessagesLoading && <LoadingAvatar color={LOADING_BLUE} height="20vh" />}
        {isFailed && <ChatEmptyText>{messagesError || t('Failed to load messages')}</ChatEmptyText>}

        {isEmpty && (
          <EmptyState>
            <EmptyIconCircle>
              <IoChatbubble size={24} />
            </EmptyIconCircle>
            <EmptyTitle>{t('No conversations yet')}</EmptyTitle>
            <EmptySubtitle>
              {t(
                'When residents at your properties send a message, their conversations will appear here.'
              )}
            </EmptySubtitle>
          </EmptyState>
        )}

        {!showMessagesLoading && !isFailed && messages.length > 0 && (
          <>
            {isLoadingOlder && (
              <ChatOlderLoadingWrap>
                <LoadingAvatar color={LOADING_BLUE} height="48px" />
              </ChatOlderLoadingWrap>
            )}
            {messages.map((message) => {
              const isSent = isMessageSent(message);
              const avatar = message.sender_avatar || '';
              const avatarName = message.sender_name || '';

              return (
                <MessageGrid key={message.id || message.client_msg_id} $sent={isSent}>
                  <AvatarSlot $sent={isSent}>
                    <ChatAvatar
                      avatar={avatar}
                      name={avatarName}
                      hoverName={avatarName}
                      size={28}
                      fontSize={11}
                    />
                  </AvatarSlot>
                  <Bubble $sent={isSent}>{message.body}</Bubble>
                  <TimeStamp $sent={isSent}>{formatRelativeTime(message.created_at)}</TimeStamp>
                </MessageGrid>
              );
            })}
          </>
        )}
      </MessagesArea>

      <ComposerBar onSubmit={handleSend}>
        <ComposerField>
          <ComposerInput
            ref={textAreaRef}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              resizeTextarea();
            }}
            onKeyDown={submitOnEnter}
            onInput={resizeTextarea}
            placeholder={t('Message')}
            rows={1}
            maxLength={MAX_CHAT_BODY_LENGTH}
            disabled={sending}
          />
          <SendButton type="submit" disabled={sending || !draft.trim()} aria-label={t('Send')}>
            <IoSendSharp size={18} />
          </SendButton>
        </ComposerField>
      </ComposerBar>
    </ThreadRoot>
  );
};

const ThreadRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: ${Palette.WHITE};
`;

const MessagesArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 1.25rem 1rem;
  ${({ $empty }) =>
    $empty
      ? `
    align-items: center;
    justify-content: center;
  `
      : ''}
`;

const EmptyState = styled.div`
  max-width: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 1rem;
`;

const EmptyIconCircle = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${Palette.SOLITUDE};
  color: ${Palette.HAZY_BLUE};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;
`;

const EmptyTitle = styled.p`
  margin: 0;
  color: ${Palette.BLACK};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
`;

const EmptySubtitle = styled.p`
  margin: 0;
  color: ${Palette.CONTENT_TERTIARY};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
`;

const MessageGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $sent }) => ($sent ? 'minmax(0, auto) 28px' : '28px minmax(0, auto)')};
  grid-template-rows: auto auto;
  column-gap: 10px;
  row-gap: 4px;
  align-self: ${({ $sent }) => ($sent ? 'flex-end' : 'flex-start')};
  max-width: 92%;
`;

const AvatarSlot = styled.div`
  grid-column: ${({ $sent }) => ($sent ? 2 : 1)};
  grid-row: 1;
  align-self: center;
  display: inline-flex;
`;

const Bubble = styled.div`
  box-sizing: border-box;
  grid-column: ${({ $sent }) => ($sent ? 1 : 2)};
  grid-row: 1;
  padding: 10px 12px;
  /* Figma: 10 / 10 / 10 / 0 on received; mirrored on sent (flat corner toward avatar) */
  border-radius: ${({ $sent }) => ($sent ? '10px 10px 0 10px' : '10px 10px 10px 0')};
  border: none;
  background: ${({ $sent }) => ($sent ? Palette.HAZY_BLUE : '#E5E5E5')};
  color: ${({ $sent }) => ($sent ? Palette.WHITE : Palette.BLACK)};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  white-space: pre-wrap;
  word-break: break-word;
  min-width: 0;
  max-width: 100%;
`;

const TimeStamp = styled.span`
  grid-column: ${({ $sent }) => ($sent ? 1 : 2)};
  grid-row: 2;
  justify-self: end;
  color: ${Palette.BLACK_50};
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 11px;
  font-weight: 400;
  line-height: 14px;
`;

const ComposerBar = styled.form`
  flex-shrink: 0;
  padding: 12px 1rem 1rem;
  background: ${Palette.GREY_100};
  border-top: 1px solid ${Palette.SOLITUDE};
`;

const ComposerField = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  background: ${Palette.WHITE};
  border: 1px solid ${Palette.STROKE_GREY};
  border-radius: 10px;
  padding: 6px 8px 6px 12px;
`;

const ComposerInput = styled.textarea`
  flex: 1;
  min-height: 28px;
  max-height: 100px;
  resize: none;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${Fonts.OPEN_SANS_FONT};
  font-size: 14px;
  line-height: 20px;
  color: ${Palette.BLACK};
  padding: 4px 0;

  &::placeholder {
    color: ${Palette.CONTENT_TERTIARY};
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${Palette.BLACK_50};
  cursor: pointer;
  flex-shrink: 0;

  &:not(:disabled) {
    color: ${Palette.HAZY_BLUE};
  }

  &:disabled {
    opacity: 0.45;
    cursor: default;
  }

  &:focus {
    outline: none;
  }
`;

export default ChatThread;
